import {
	deferred,
	isNil,
	isNotNil
} from "client/Misc/utils";

export type ImageDimensions = {
	width: number;
	height: number;
};

export type ImageDimensionsChange = {
	from: ImageDimensions;
	to: ImageDimensions;
};

export type CompressedImage = {
	file: File;
	dimensions: ImageDimensionsChange;
	didRemoveTransparency: boolean;
};

export type ImageCompressionResult = {
	kind: "compressed";
	image: CompressedImage;
} | {
	kind: "unsupported-format";
} | {
	kind: "failed";
};

export async function compressImageToFitFileSize(
	file: File,
	maxFileSize: number
): Promise<ImageCompressionResult> {
	if (!canCompressImage(file.type)) {
		return { kind: "unsupported-format" };
	}

	try {
		const originalDimensions: ImageDimensions = await getImageDimensions(file);

		for (const attempt of COMPRESSION_ATTEMPTS) {
			const targetDimensions: ImageDimensions = scaleToFitLongestEdge(
				originalDimensions,
				attempt.maxLongestEdge
			);

			const encoded: EncodedImage | null = await encodeAtDimensions({
				file,
				dimensions: targetDimensions,
				quality: attempt.quality
			});

			if (isNil(encoded)) {
				continue;
			}

			const compressedSize: number = encoded.file
				.size;

			if (compressedSize <= maxFileSize
				&& compressedSize < file.size
			) {
				return {
					kind: "compressed",
					image: {
						file: encoded.file,
						dimensions: {
							from: originalDimensions,
							to: encoded.dimensions
						},
						didRemoveTransparency: encoded.didRemoveTransparency
					}
				};
			}
		}

		return { kind: "failed" };
	} catch (e) {
		console.error(e);

		return { kind: "failed" };
	}
}

export function canCompressImage(mimeType: string): boolean {
	return COMPRESSIBLE_MIME_TYPES.includes(mimeType);
}

function scaleToFitLongestEdge(
	dimensions: ImageDimensions,
	maxLongestEdge: number
): ImageDimensions {
	const longestEdge: number = Math.max(dimensions.width, dimensions.height);

	if (longestEdge <= maxLongestEdge) {
		return dimensions;
	}

	const scale: number = maxLongestEdge / longestEdge;

	return {
		width: Math.max(
			Math.round(dimensions.width * scale),
			1
		),
		height: Math.max(
			Math.round(dimensions.height * scale),
			1
		)
	};
}

type EncodedImage = {
	file: File;
	dimensions: ImageDimensions;
	didRemoveTransparency: boolean;
};

type EncodeAtDimensionsInput = {
	file: File;
	dimensions: ImageDimensions;
	quality: number;
};

const TRANSPARENCY_BACKGROUND_COLOR: string = "#ffffff";

const JPEG_MIME_TYPE: string = "image/jpeg";

const PNG_MIME_TYPES: readonly string[] = [
	"image/png",
	"image/x-png"
];

const COMPRESSIBLE_MIME_TYPES: readonly string[] = [
	JPEG_MIME_TYPE,
	"image/jpg",
	"image/pjpeg",
	...PNG_MIME_TYPES
];

type CompressionAttempt = {
	maxLongestEdge: number;
	quality: number;
};

const COMPRESSION_ATTEMPTS: readonly CompressionAttempt[] = [
	{
		maxLongestEdge: 2560,
		quality: 0.8
	},
	{
		maxLongestEdge: 1920,
		quality: 0.75
	},
	{
		maxLongestEdge: 1280,
		quality: 0.7
	}
];

function getImageDimensions(file: File): Promise<ImageDimensions> {
	const {
		promise: result,
		resolve,
		reject
	} = deferred<ImageDimensions>();

	const objectUrl: string = URL.createObjectURL(file);
	const image = new Image();

	image.onload = (): void => {
		URL.revokeObjectURL(objectUrl);
		resolve({
			width: image.naturalWidth,
			height: image.naturalHeight
		});
	};

	image.onerror = (): void => {
		URL.revokeObjectURL(objectUrl);
		reject(new Error("The image could not be read"));
	};

	image.src = objectUrl;

	return result;
}

async function encodeAtDimensions(
	input: EncodeAtDimensionsInput
): Promise<EncodedImage | null> {
	const {
		file,
		dimensions,
		quality
	} = input;

	const bitmap: ImageBitmap = await createImageBitmap(
		file,
		{
			imageOrientation: "from-image",
			resizeWidth: dimensions.width,
			resizeHeight: dimensions.height,
			resizeQuality: "high"
		}
	);

	const canvas: HTMLCanvasElement = document.createElement("canvas");
	canvas.width = dimensions.width;
	canvas.height = dimensions.height;

	const context: CanvasRenderingContext2D | null = canvas.getContext("2d");

	if (isNil(context)) {
		bitmap.close();
		return null;
	}

	context.imageSmoothingEnabled = true;
	context.imageSmoothingQuality = "high";

	context.drawImage(
		bitmap,
		0,
		0,
		dimensions.width,
		dimensions.height
	);

	bitmap.close();

	const didRemoveTransparency: boolean = PNG_MIME_TYPES.includes(file.type)
		&& containsTransparency(context, dimensions);

	if (didRemoveTransparency) {
		context.globalCompositeOperation = "destination-over";
		context.fillStyle = TRANSPARENCY_BACKGROUND_COLOR;
		context.fillRect(
			0,
			0,
			dimensions.width,
			dimensions.height
		);
	}

	const blob: Blob | null = await encodeCanvasAsJpeg(canvas, quality);

	if (isNil(blob)) {
		return null;
	}

	return {
		file: new File(
			[blob],
			changeExtensionToJpeg(file.name),
			{ type: JPEG_MIME_TYPE }
		),
		dimensions,
		didRemoveTransparency
	};
}

function encodeCanvasAsJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
	const {
		promise: result,
		resolve
	} = deferred<Blob | null>();

	canvas.toBlob(
		(blob: Blob | null): void => {
			resolve(
				isNotNil(blob) && blob.type === JPEG_MIME_TYPE
					? blob
					: null
			);
		},
		JPEG_MIME_TYPE,
		quality
	);

	return result;
}

function containsTransparency(
	context: CanvasRenderingContext2D,
	dimensions: ImageDimensions
): boolean {
	const { data } = context.getImageData(
		0,
		0,
		dimensions.width,
		dimensions.height
	);

	for (let index = 3; index < data.length; index += 4) {
		if (data[index] !== 255) {
			return true;
		}
	}

	return false;
}

function changeExtensionToJpeg(fileName: string): string {
	const lastDotIndex: number = fileName.lastIndexOf(".");

	return lastDotIndex <= 0
		? `${fileName}.jpg`
		: `${fileName.slice(0, lastDotIndex)}.jpg`;
}
